import React, { useContext, useEffect, useRef, useState } from "react";
import {
  DefaultButton,
  PrimaryButton,
  TextField,
  Spinner,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import ModalMain from "../Modal/ModalMain";
import ModalHeader from "../Modal/ModalHeader";
import themeContext from "../../context/theme/ThemeContext";
import { t } from "i18next";
import userContext from "../../context/users/UserContext";
import { encryptData } from "../../helpers/RSAUtility";

// 👉 Replace this with your real service call
// Should resolve { status: 200 } on success, throw or return non-200 on failure
// import { authenticateUser } from "../../service/AuthService";

// Props for PasswordAuthModal:
// visible: boolean
// username: string
// onClose: function
// onResult?: function (optional)
// title?: string (optional)

const PasswordAuthModal = ({
  visible,
  username,
  onClose,
  onResult,
  title = "Authentication Required",
}) => {
  const { getThemeColor } = useContext(themeContext);
  const { signin, loadByUsername , authenticated} = useContext(userContext);
  
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setPassword("");
      setError(null);
      setSubmitting(false);
      // focus password field when modal opens
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [visible]);

  const closeWith = (ok) => {
    // notify result first (parent may unmount modal inside onClose)
    onResult?.(ok);
    onClose();
  };

  const handleCancel = () => {
    if (!submitting) closeWith(false);
  };

  const handleSubmit = async () => {
    if (!password || submitting) return;

    setSubmitting(true);
    setError(null);

    try {

      const encryptedUsername = encryptData(username);
              const encryptedPassword = encryptData(password);      
              const res = await signin(encryptedUsername, encryptedPassword);

      if (res?.status === 200) {
        closeWith(true);
      } else {
        setError("Authentication failed.");
        setSubmitting(false);
      }
    } catch (e) {
      setError(t("string-literals.failed-message") || "Authentication failed.");
      setSubmitting(false);
    }
  };

  // Submit on Enter
  const onKeyDown = (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      handleSubmit();
    }
  };

  if (!visible) return null;

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal
        heading={title}
        handleCancel={handleCancel}
      />
    ),
    body: (
      <div style={{ padding: "3%", fontWeight: 500 }}>
        <div style={{ marginBottom: 12 }}>
          {t("string-literals.password-auth-prompt", {
            defaultValue: "Please enter the password for {{username}}.",
            username,
          })}
        </div>

        <TextField
          componentRef={(ref) => {
            // @ts-ignore: Fluent UI types; ref?.element is the input element
            inputRef.current = ref?.element || null;
          }}
          value={password}
          onChange={(_, v) => setPassword(v || "")}
          onKeyDown={onKeyDown}
          type="password"
          canRevealPassword
          placeholder={"Password"}
          disabled={submitting}
          styles={{ root: { marginTop: 6 } }}
        />

        {error && (
          <div style={{ marginTop: 12 }}>
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              {error}
            </MessageBar>
          </div>
        )}

        {submitting && (
          <div style={{ display: "flex", alignItems: "center", marginTop: 12 }}>
            <Spinner />
            <span style={{ marginLeft: 8 }}>
              {"Authenticating..."}
            </span>
          </div>
        )}
      </div>
    ),
    footer: (
      <div
        className="modal-footer-container"
        style={{ background: getThemeColor("modal-header-footer") }}
      >
        <div className="confirm-modal-footer">
          <DefaultButton text={"Cancel"} onClick={handleCancel} disabled={submitting} />
          <PrimaryButton
            text={"Confirm"}
            style={{ marginLeft: 8 }}
            onClick={handleSubmit}
            disabled={!password || submitting}
          />
        </div>
      </div>
    ),
    width: "480px",
  };

  return <ModalMain showModal={visible} modalDetails={modalDetails} />;
};

export default PasswordAuthModal;
